import React from 'react';
import {
	Text,
	InputAutocompleteMulti,
	InfoCard,
	Button,
	StylovyzeTooltip,
	palette,
	LoadingPage,
} from '@innovyze/stylovyze';
import { Box, Radio, Stack, Typography } from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { useGlobalization } from '@Translations/useGlobalization';
import { Field, useFormikContext } from 'formik';
import { useAssetSchema, useSystemType } from '@Hooks/assetSchemaHooks';
import { ConditionScoreFilter } from './ConditionScoreFilter';
import { DefectCodeFilter } from './DefectCodesFilter';
import { TypeWithDateFieldSelector } from './TypeWithDateFieldSelector';
import { SystemTypeField, AssetTypeField, InspectionStandardField } from './SystemInformationFields';
import { FailureDefinitionFormValues } from '@Pages/FailureDefinition';
import { Help } from '@mui/icons-material';
import { selectFailureDefinitionProcess } from '@Selectors/failureDefinitions.selector';
import { WEAVE_FONT } from '@Utils/fonts';

interface TypeWithFields {
	uploadId: string;
	name: string;
	fields: { key: string; type: string; display_name: string }[];
}

interface DefectCode {
	code: string;
	desc: string;
}

interface InspectionOption {
	key: string;
	value: string;
}

interface FailureDefinitionConfigurationProps {
	eventTypeInformation: TypeWithFields[];
	taskTypeInformation: TypeWithFields[];
	inspectionOptions: InspectionOption[];
	defectCodeTypes: DefectCode[];
	isCreating?: boolean;
	rehabMetadataLoading?: boolean;
	onSystemInfoChange?: (systemType: string, assetType: string, inspectionStandard: string) => void;
}

const PRIORITIZED_CATEGORIES = {
	EVENT: 'event',
	INSPECTION: 'inspection',
	TASK: 'task',
} as const;

const INSPECTION_TYPES = {
	CONDITION_SCORE: 'conditionScore',
	DEFECT_CODES: 'defectCodes',
} as const;

interface PrioritizedCardTitleProps {
	title: string;
	helperText: string | undefined;
	categoryValue: string;
	onToggle: (value: string) => void;
	t: (key: string) => string;
}

const PrioritizedCardTitle: React.FC<PrioritizedCardTitleProps> = ({
	title,
	helperText,
	categoryValue,
	onToggle,
	t,
}) => (
	<Stack direction="row" alignItems="flex-start" spacing={1}>
		<Box
			sx={{
				width: 5,
				minHeight: 40,
				bgcolor: 'primary.main',
			}}
		/>
		<Stack spacing={0}>
			<Stack direction="row" spacing={1} alignItems="center">
				<Text variant="heading-medium-strong" sx={{ mb: 0, lineHeight: 1.2 }}>
					{title}
				</Text>
				<StylovyzeTooltip heading={title} content={helperText}>
					<Help sx={{ fontSize: 'medium', color: palette.secondary.main }} />
				</StylovyzeTooltip>
			</Stack>
			<Stack direction="row" alignItems="center" spacing={0.25} sx={{ ml: -0.2 }}>
				<Field
					type="radio"
					name="prioritizedCategory"
					value={categoryValue}
					as={Radio}
					size="small"
					onClick={() => onToggle(categoryValue)}
					icon={<RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />}
					checkedIcon={<RadioButtonCheckedIcon sx={{ fontSize: 16 }} />}
					sx={{ p: 0, m: 0 }}
				/>
				<Text variant="caption-medium" sx={{ color: 'text.secondary', fontSize: 13 }}>
					{t('Prioritize this failure type if multiple failures exist in an asset.')}
				</Text>
			</Stack>
		</Stack>
	</Stack>
);

export const FailureDefinitionConfiguration: React.FC<FailureDefinitionConfigurationProps> = ({
	eventTypeInformation,
	taskTypeInformation,
	inspectionOptions,
	defectCodeTypes,
	isCreating = false,
	rehabMetadataLoading = false,
	onSystemInfoChange,
}) => {
	const { t } = useGlobalization();
	const { values, setFieldValue, validateForm, submitForm } = useFormikContext<FailureDefinitionFormValues>();
	const { isUpsertInProcess, isRunInProcess } = selectFailureDefinitionProcess();

	// Display reads use the open-flag hooks. While pending or on error the raw ids are shown.
	const { data: systemType } = useSystemType(values.systemType);
	const { data: assetSchema, status: assetSchemaStatus } = useAssetSchema(values.systemType, values.assetType);
	const systemTypeDisplayName = systemType?.displayName ? t(systemType.displayName) : values.systemType;
	const assetTypeDisplayName = assetSchema?.displayName ? t(assetSchema.displayName) : values.assetType;
	const isRunning = isUpsertInProcess || isRunInProcess;
	/** lib-asset-schema: selected asset type must define inspection standards (capability + types). */
	const showInspectionConfiguration = React.useMemo(() => {
		if (!assetSchema) {
			return false;
		}
		return assetSchema.hasCapability('Inspections') && assetSchema.getInspectionTypes().length > 0;
	}, [assetSchema]);
	const prevEventTypeInfoRef = React.useRef(eventTypeInformation);
	const prevTaskTypeInfoRef = React.useRef(taskTypeInformation);
	const prevSystemTypeRef = React.useRef(values.systemType);
	const prevShowInspectionConfigurationRef = React.useRef<boolean | undefined>(undefined);

	// When the selected asset schema does not support inspections, clear stale inspection form state.
	React.useEffect(() => {
		// Don't treat a schema that is still loading as "inspections unsupported", or loaded values would be wiped.
		if (values.assetType && assetSchemaStatus === 'pending') {
			return;
		}
		if (showInspectionConfiguration) {
			prevShowInspectionConfigurationRef.current = true;
			return;
		}

		const prevShow = prevShowInspectionConfigurationRef.current;
		prevShowInspectionConfigurationRef.current = false;

		const shouldClearInspectionState = prevShow === undefined || prevShow === true;

		if (shouldClearInspectionState) {
			setFieldValue('inspections', []);
			setFieldValue('conditionScoreKey', '');
			setFieldValue('conditionScoreOperator', '');
			setFieldValue('conditionScoreValue', 0);
			setFieldValue('conditionScoreValueTo', 0);
			setFieldValue('defectCodes', []);
			if (values.prioritizedCategory === 'inspection') {
				setFieldValue('prioritizedCategory', '');
			}
		}
	}, [showInspectionConfiguration, assetSchemaStatus, values.assetType, setFieldValue, values.prioritizedCategory]);

	React.useEffect(() => {
		const systemTypeChanged = prevSystemTypeRef.current !== values.systemType;
		prevSystemTypeRef.current = values.systemType;

		// Skip API call if systemType just changed - wait for assetType to be reset first
		if (systemTypeChanged) {
			return;
		}

		// Wait for the schema before fetching metadata. Otherwise the fetch fires once while inspections are unknown and
		// again when the schema resolves, and each fetch swaps the form for the loading page.
		if (values.assetType && assetSchemaStatus === 'pending') {
			return;
		}

		const needsInspectionStandard = showInspectionConfiguration;
		const hasRequiredFields =
			values.systemType && values.assetType && (!needsInspectionStandard || values.inspectionStandard);

		if (hasRequiredFields && onSystemInfoChange) {
			onSystemInfoChange(values.systemType, values.assetType, values.inspectionStandard);
		}
	}, [
		values.systemType,
		values.assetType,
		values.inspectionStandard,
		onSystemInfoChange,
		showInspectionConfiguration,
		assetSchemaStatus,
	]);

	// Reset selected events if they don't belong to the current system type (only when type info changes)
	React.useEffect(() => {
		const prevEventTypeInfo = prevEventTypeInfoRef.current;
		const eventTypeInfoChanged = prevEventTypeInfo !== eventTypeInformation && prevEventTypeInfo.length > 0;

		if (eventTypeInfoChanged && eventTypeInformation.length > 0 && values.events.length > 0) {
			const validEventNames = eventTypeInformation.map(e => e.name);
			const validSelectedEvents = values.events.filter(event => validEventNames.includes(event));

			if (validSelectedEvents.length !== values.events.length) {
				setFieldValue('events', validSelectedEvents);
				// Also clean up the eventDateMap for removed events
				const newEventDateMap: Record<string, string> = {};
				validSelectedEvents.forEach(event => {
					if (values.eventDateMap[event]) {
						newEventDateMap[event] = values.eventDateMap[event];
					}
				});
				setFieldValue('eventDateMap', newEventDateMap);
			}
		}

		prevEventTypeInfoRef.current = eventTypeInformation;
	}, [eventTypeInformation, values.events, values.eventDateMap, setFieldValue]);

	// Reset selected tasks if they don't belong to the current system type (only when type info changes)
	React.useEffect(() => {
		const prevTaskTypeInfo = prevTaskTypeInfoRef.current;
		const taskTypeInfoChanged = prevTaskTypeInfo !== taskTypeInformation && prevTaskTypeInfo.length > 0;

		if (taskTypeInfoChanged && taskTypeInformation.length > 0 && values.tasks.length > 0) {
			const validTaskNames = taskTypeInformation.map(t => t.name);
			const validSelectedTasks = values.tasks.filter(task => validTaskNames.includes(task));

			if (validSelectedTasks.length !== values.tasks.length) {
				setFieldValue('tasks', validSelectedTasks);
				// Also clean up the taskDateMap for removed tasks
				const newTaskDateMap: Record<string, string> = {};
				validSelectedTasks.forEach(task => {
					if (values.taskDateMap[task]) {
						newTaskDateMap[task] = values.taskDateMap[task];
					}
				});
				setFieldValue('taskDateMap', newTaskDateMap);
			}
		}

		prevTaskTypeInfoRef.current = taskTypeInformation;
	}, [taskTypeInformation, values.tasks, values.taskDateMap, setFieldValue]);

	const handlePriorityToggle = React.useCallback(
		(categoryValue: string) => {
			const newValue = values.prioritizedCategory === categoryValue ? '' : categoryValue;
			setFieldValue('prioritizedCategory', newValue);
		},
		[values.prioritizedCategory, setFieldValue],
	);

	const handleFormSubmit = React.useCallback(async () => {
		try {
			const errors = await validateForm();
			const isValid = Object.keys(errors).length === 0;
			if (isValid) {
				submitForm();
			}
		} catch (error) {
			console.error('Form validation error:', error);
		}
	}, [validateForm, submitForm]);

	if (rehabMetadataLoading) {
		return <LoadingPage />;
	}

	return (
		<Stack spacing={3} sx={{ fontFamily: WEAVE_FONT }}>
			<Box sx={{ width: 'fit-content', maxWidth: '100%' }}>
				<Stack spacing={0.5}>
					<Typography
						component="h2"
						sx={{ fontFamily: WEAVE_FONT, fontSize: '1.25rem', fontWeight: 700, color: '#111827', lineHeight: 1.4 }}>
						{t('Failure Definition Configuration')}
					</Typography>
					<Typography sx={{ fontFamily: WEAVE_FONT, fontSize: '0.8125rem', color: '#6b7280' }}>
						{t(
							'Add one or more parameters that indicate failure in your {{systemLabel}}. An asset is considered to have failed if it matches any of these parameters, and its failure date will be the earliest date any of these occurred. At least one parameter is required.',
							{
								systemLabel:
									systemTypeDisplayName && assetTypeDisplayName
										? `${systemTypeDisplayName} ${assetTypeDisplayName}`
										: t('selected asset type'),
							},
						)}
					</Typography>
				</Stack>
			</Box>
			{isCreating && (
				<Box sx={{ width: 'fit-content', maxWidth: '100%' }}>
					<InfoCard title={t('System Information')}>
						<Stack
							direction="row"
							spacing={3}
							sx={{
								alignItems: 'flex-start',
							}}>
							<Box sx={{ width: '250px', flexShrink: 0 }}>
								<SystemTypeField />
							</Box>
							<Box sx={{ width: '250px', flexShrink: 0 }}>
								<AssetTypeField />
							</Box>
							<Box sx={{ width: '250px', flexShrink: 0 }}>
								{showInspectionConfiguration && <InspectionStandardField />}
							</Box>
						</Stack>
					</InfoCard>
				</Box>
			)}
			<Stack>
				<InfoCard
					title={
						<PrioritizedCardTitle
							title="Events"
							helperText={t(
								'Events are incidents that have occurred to your assets. Certain incidents may indicate that an asset failed. For example, flooding, bursts, blockages, etc. You can add any events that indicate asset failure.',
							)}
							categoryValue={PRIORITIZED_CATEGORIES.EVENT}
							onToggle={handlePriorityToggle}
							t={t}
						/>
					}>
					<TypeWithDateFieldSelector
						typeInformation={eventTypeInformation}
						label={t('Events')}
						typeFieldName="events"
						dateFieldMapFieldName="eventDateMap"
						required
					/>
				</InfoCard>
			</Stack>
			{showInspectionConfiguration && (
				<Stack spacing={3}>
					<InfoCard
						title={
							<PrioritizedCardTitle
								title="Inspections"
								helperText={t(
									'Condition data from inspections can indicate asset failure, for example, if an asset has a severe condition score or specific defect codes (collapse, break, etc.). You can specify the condition score and/or defect codes that indicate your assets field.',
								)}
								categoryValue={PRIORITIZED_CATEGORIES.INSPECTION}
								onToggle={handlePriorityToggle}
								t={t}
							/>
						}>
						<InputAutocompleteMulti
							fieldAttrs={{ name: 'inspections' }}
							options={inspectionOptions}
							label={t('Inspections')}
							textFieldProps={{
								required: true,
								fullWidth: true,
								sx: { maxWidth: '550px' },
							}}
						/>
						{values.inspections?.includes(INSPECTION_TYPES.CONDITION_SCORE) && (
							<ConditionScoreFilter inspectionStandard={values.inspectionStandard} />
						)}
						{values.inspections?.includes(INSPECTION_TYPES.DEFECT_CODES) && (
							<DefectCodeFilter defectCodes={defectCodeTypes} />
						)}
					</InfoCard>
				</Stack>
			)}
			<Stack>
				<InfoCard
					title={
						<PrioritizedCardTitle
							title="Tasks"
							helperText={t(
								'Tasks are repair or maintenance activities that have been performed on assets. If certain types of repair were necessary, this may indicate that the asset failed. For example, relining, replacement, etc. You can add any tasks that indicate asset failure.',
							)}
							categoryValue={PRIORITIZED_CATEGORIES.TASK}
							onToggle={handlePriorityToggle}
							t={t}
						/>
					}>
					<TypeWithDateFieldSelector
						typeInformation={taskTypeInformation}
						label={t('Tasks')}
						typeFieldName="tasks"
						dateFieldMapFieldName="taskDateMap"
						required
					/>
				</InfoCard>
			</Stack>
			<Button
				type="submit"
				variant="contained"
				color="primary"
				sx={{ alignSelf: 'start' }}
				disabled={isRunning}
				onClick={handleFormSubmit}>
				{isRunning ? t('Running') : t('Run')}
			</Button>
			<Box sx={{ height: 40 }} />
		</Stack>
	);
};
